import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HelpCircle,
  LogOut,
  ChevronDown,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useBusiness } from '../contexts/BusinessContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useMenuConfig } from '../hooks/useMenuConfig';
import { getIcon } from '../utils/iconMapping';
import { LAYOUT } from '../constants/layout';

const SIDEBAR_STATE_KEY = 'billsera_sidebar_state';

const ResponsiveSidebar = ({ isOpen, onClose, isMobile = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { activeBusiness } = useBusiness();
  const { isOwner, isStaff, loading: permissionsLoading } = usePermissions();
  
  // Fetch dynamic menu from backend API
  const { menu, planName, loading: menuLoading, error: menuError } = useMenuConfig();

  // Helper to find which group a path belongs to
  const getGroupForPath = (path) => {
    if (!menu || menu.length === 0) return null;
    
    // Find the module that contains this path
    for (const module of menu) {
      if (module.route && path.includes(module.route)) {
        return module.key;
      }
      if (module.children) {
        for (const child of module.children) {
          if (child.route && path.includes(child.route)) {
            return module.key;
          }
        }
      }
    }
    
    // Default to first module
    return menu[0]?.key || null;
  };

  // Manage expanded groups
  const [expandedGroup, setExpandedGroup] = useState(() => {
    return getGroupForPath(location.pathname);
  });

  // Update expanded group when location or menu changes
  useEffect(() => {
    if (menu && menu.length > 0) {
      const currentGroup = getGroupForPath(location.pathname);
      if (currentGroup) {
        setExpandedGroup(currentGroup);
      }
    }
  }, [location.pathname, menu]);

  const [expandedSubmenus, setExpandedSubmenus] = useState(() => {
    try {
      const saved = localStorage.getItem(SIDEBAR_STATE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return new Set(parsed.expandedSubmenus || []);
      }
    } catch (e) {
      // Ignore localStorage errors
    }
    return new Set();
  });

  // Save submenu state
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify({
        expandedSubmenus: Array.from(expandedSubmenus)
      }));
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [expandedSubmenus]);

  // Show loading state
  if (permissionsLoading || menuLoading) {
    return (
      <div className={`bg-white border-r border-gray-200 flex flex-col ${isMobile ? 'fixed inset-y-0 left-0 z-50 w-72 transform transition-transform duration-300 ease-in-out' : 'w-72'}`}>
        <div className="flex items-center justify-center h-full bg-[#181A1F]">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-gray-600 border-t-teal-500 rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-sm text-gray-400">Loading menu...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if menu failed to load
  if (menuError && (!menu || menu.length === 0)) {
    return (
      <div className={`bg-[#181A1F] border-r border-gray-700 flex flex-col ${isMobile ? 'fixed inset-y-0 left-0 z-50 w-72' : 'w-72'}`}>
        <div className="flex items-center justify-center h-full">
          <div className="text-center p-4">
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-3">
              <X className="w-6 h-6 text-red-400" />
            </div>
            <p className="text-sm text-gray-400 mb-2">Failed to load menu</p>
            <button
              onClick={() => window.location.reload()}
              className="text-xs text-teal-400 hover:text-teal-300"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleNavigation = (route) => {
    if (!route) return;
    
    // Build full path with business context
    let fullPath = route;
    if (activeBusiness?.id && !route.startsWith('/business/')) {
      fullPath = `/business/${activeBusiness.id}${route}`;
    }
    
    navigate(fullPath);
    
    if (isMobile && onClose) {
      onClose();
    }
  };

  const isActive = (route) => {
    if (!route || !activeBusiness?.id) return false;
    const fullPath = `/business/${activeBusiness.id}${route}`;
    return location.pathname === fullPath || location.pathname.startsWith(fullPath + '/');
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroup(expandedGroup === groupKey ? null : groupKey);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Check if a group has an active child
  const hasActiveChild = (module) => {
    if (module.route && isActive(module.route)) return true;
    if (module.children) {
      return module.children.some(child => isActive(child.route));
    }
    return false;
  };

  const renderMenuItem = (item, level = 0) => {
    const Icon = getIcon(item.icon);
    const active = isActive(item.route);

    const paddingClass = level === 0 ? 'pl-3 pr-3 py-2.5' : 'pl-8 pr-3 py-2';
    const textSizeClass = 'text-[13px]';
    const iconSizeClass = level === 0 ? 'w-[18px] h-[18px]' : 'w-[15px] h-[15px]';
    
    return (
      <button
        key={item.key || item.route}
        onClick={() => handleNavigation(item.route)}
        data-testid={`sidebar-item-${item.key || item.route?.replace(/\//g, '-')}`}
        className={`w-full flex flex-row items-center gap-2.5 ${paddingClass} text-left transition-all duration-200 rounded-md relative ${
          active
            ? 'bg-teal-600/20 text-teal-400'
            : 'text-gray-300 hover:bg-[#2A2D35] hover:text-white'
        }`}
        style={{ display: 'flex', alignItems: 'center' }}
      >
        {/* Active indicator line */}
        {active && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-[60%] bg-teal-500 rounded-r-full" />
        )}
        {Icon && <Icon className={`${iconSizeClass} flex-shrink-0 ${active ? 'text-teal-400' : ''}`} style={{ display: 'block' }} />}
        <span className={`${textSizeClass} leading-none ${active ? 'font-medium' : ''}`} style={{ display: 'block', lineHeight: '15px' }}>{item.label}</span>
      </button>
    );
  };

  const renderGroup = (module) => {
    const isExpanded = expandedGroup === module.key;
    const GroupIcon = getIcon(module.icon);
    const groupHasActiveChild = hasActiveChild(module);
    const hasChildren = module.children && module.children.length > 0;

    // If module has a direct route and no children, render as a simple item
    if (module.route && !hasChildren) {
      return (
        <div key={module.key} className="mb-1">
          {renderMenuItem(module, 0)}
        </div>
      );
    }

    return (
      <div key={module.key} className="mb-1" data-testid={`sidebar-group-${module.key}`}>
        {/* Group Header */}
        <button
          onClick={() => toggleGroup(module.key)}
          className={`w-full flex flex-row items-center justify-between px-3 py-2.5 text-left premium-transition-colors rounded-lg relative ${
            isExpanded
              ? 'bg-[#2A2D35] text-white'
              : groupHasActiveChild
                ? 'bg-[#252830] text-teal-400'
                : 'text-gray-300 hover:bg-[#2A2D35] hover:text-white'
          }`}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          {/* Active parent indicator */}
          {(isExpanded || groupHasActiveChild) && (
            <span className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-[70%] rounded-r-full ${
              isExpanded ? 'bg-teal-500' : 'bg-teal-500/50'
            }`} />
          )}
          <div className="flex flex-row items-center gap-2.5 flex-1" style={{ display: 'flex', alignItems: 'center' }}>
            {GroupIcon && <GroupIcon className={`w-[17px] h-[17px] flex-shrink-0 ${groupHasActiveChild ? 'text-teal-400' : ''}`} style={{ display: 'block' }} />}
            <span className={`text-[13px] leading-none ${isExpanded || groupHasActiveChild ? 'font-medium' : ''}`} style={{ display: 'block', lineHeight: '17px' }}>{module.label}</span>
          </div>
          <ChevronDown className={`w-4 h-4 transition-transform duration-300 ease-out flex-shrink-0 ml-2 ${
            isExpanded ? 'rotate-0' : '-rotate-90'
          }`} style={{ display: 'block' }} />
        </button>

        {/* Group Content with smooth accordion */}
        <div
          className={`overflow-hidden ${
            isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
          style={{
            transition: 'max-height 0.25s ease-in-out, opacity 0.2s ease-in-out'
          }}
        >
          <div className="pl-1 mt-2 pr-1">
            {hasChildren && module.children.map((child) => renderMenuItem(child, 1))}
          </div>
        </div>
      </div>
    );
  };

  const sidebarContent = (
    <div className="flex flex-col h-full bg-[#181A1F] text-white">
      {/* Header */}
      <div className="p-4 border-b border-gray-700/50 flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold text-[15px] tracking-tight">Billsera</h2>
          {activeBusiness && (
            <p className="text-[11px] text-gray-400 mt-0.5 truncate font-normal">
              {activeBusiness.name}
            </p>
          )}
          {planName && (
            <span className="inline-block mt-1 px-2 py-0.5 text-[9px] bg-teal-500/20 text-teal-400 rounded-full">
              {planName}
            </span>
          )}
        </div>
        {isMobile && onClose && (
          <button
            onClick={onClose}
            className="lg:hidden p-2 hover:bg-gray-700 rounded flex-shrink-0 ml-2"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Dynamic Menu Items */}
      <div className="flex-1 overflow-y-auto py-4 scrollbar-custom">
        {menu && menu.length > 0 ? (
          menu.map(module => renderGroup(module))
        ) : (
          <div className="px-4 py-8 text-center text-gray-500">
            <p className="text-sm">No menu items available</p>
          </div>
        )}
      </div>

      {/* Fixed Bottom Actions */}
      <div className="border-t border-gray-700/50 p-2">
        <button
          className="w-full flex items-center gap-2.5 px-3 py-2.5 text-gray-400 hover:bg-[#2A2D35] hover:text-gray-200 rounded-lg transition-colors"
          data-testid="sidebar-help"
        >
          <HelpCircle className="w-4 h-4" />
          <span className="text-[13px] font-normal">Help & Support</span>
        </button>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 text-red-400/80 hover:bg-red-900/20 hover:text-red-400 rounded-lg transition-colors"
          data-testid="sidebar-logout"
        >
          <LogOut className="w-4 h-4" />
          <span className="text-[13px] font-normal">Logout</span>
        </button>
      </div>
    </div>
  );

  // Desktop - sidebar with rounded corners
  if (!isMobile) {
    return (
      <div className={`${LAYOUT.SIDEBAR_WIDTH_CLASS} h-full rounded-lg shadow-xl overflow-hidden`}>
        {sidebarContent}
      </div>
    );
  }

  // Mobile - return content only
  return sidebarContent;
};

export default ResponsiveSidebar;
